import {Injectable, signal} from '@angular/core';
import {lastValueFrom, startWith} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {Task, TaskCountsResponse, TaskFilterParams, TaskFiltersResponse, TaskPaginatedResponse} from '../../models/task';
import {environment} from '../../environments/environment';
import {TaskRelationship} from '../../models/task-relationship';
import {RealtimeService} from './realtime.service';
import {ulid} from 'ulid';
import {sanitizeDelta} from '../utils';
import {PaginationInfo} from '../../models/la';

@Injectable({
  providedIn: 'root'
})
export class TaskService {

  taskList = signal<Task[]>([]);
  taskPagination = signal<PaginationInfo | null>(null);
  taskFilters = signal<TaskFiltersResponse | null>(null);
  taskCounts = signal<TaskCountsResponse | null>(null);
  tasksForDropdown = signal<Task[]>([]);
  allRelationships = signal<TaskRelationship[]>([]);

  url = environment.backendUrl;

  private currentParams: TaskFilterParams = { limit: 10, offset: 0, status: ['open', 'inProgress'] };

  constructor(
    private realtime: RealtimeService,
    private http: HttpClient
  ) {
    this.realtime.tasks.pipe(startWith(null))
      .subscribe(() => {
        this.refreshCurrentPage();
        this.getAllTaskRelationships();
    })
  }

  async refreshCurrentPage() {
    await this.getTasksPaginated(this.currentParams);
  }

  async getTasksPaginated(params: TaskFilterParams = {}): Promise<TaskPaginatedResponse> {
    this.currentParams = {
      limit: params.limit ?? this.currentParams.limit,
      offset: params.offset ?? this.currentParams.offset,
      search: params.search || undefined,
      users: params.users?.length ? params.users : undefined,
      status: params.status?.length ? params.status : undefined,
      deadline: params.deadline || undefined,
      sortBy: params.sortBy || this.currentParams.sortBy,
      sortOrder: params.sortOrder || this.currentParams.sortOrder,
    };
    const queryParams = new URLSearchParams();
    if (this.currentParams.limit !== undefined) queryParams.set('limit', this.currentParams.limit.toString());
    if (this.currentParams.offset !== undefined) queryParams.set('offset', this.currentParams.offset.toString());
    if (this.currentParams.search) queryParams.set('search', this.currentParams.search);
    if (this.currentParams.users && this.currentParams.users.length > 0) queryParams.set('users', this.currentParams.users.join(','));
    if (this.currentParams.status && this.currentParams.status.length > 0) queryParams.set('status', this.currentParams.status.join(','));
    if (this.currentParams.deadline) queryParams.set('deadline', this.currentParams.deadline);
    if (this.currentParams.sortBy) queryParams.set('sortBy', this.currentParams.sortBy);
    if (this.currentParams.sortOrder) queryParams.set('sortOrder', this.currentParams.sortOrder);
    const queryString = queryParams.toString();
    const url = `${this.url}/v2/tasks${queryString ? `?${queryString}` : ''}`;
    const result = await lastValueFrom(this.http.get<TaskPaginatedResponse>(url));
    this.taskList.set(result.data);
    this.taskPagination.set(result.pagination);
    this.taskFilters.set(result.filters);
    this.taskCounts.set(result.counts);
    return result;
  }

  async getTaskById(taskId: string): Promise<Task> {
    return await lastValueFrom(this.http.get<Task>(`${this.url}/tasks/${taskId}`));
  }

  async getAllTasks() {
    const results = await lastValueFrom(this.http.get<Task[]>(this.url + '/tasks'));
    this.tasksForDropdown.set(results);
    return results;
  }

  async updateTaskDescription(id: string, description: string) {
    await lastValueFrom(this.http.put<Task>(`${this.url}/tasks/${id}/description`, {description}));
    await this.refreshCurrentPage();
  }

  async updateTaskDelta(id: string, delta: any) {
    await lastValueFrom(this.http.put<Task>(`${this.url}/tasks/${id}/delta`, {delta: delta.ops}));
    await this.refreshCurrentPage();
  }

  async updateTaskAssignee(id: string, assigneeUserId: string) {
    await lastValueFrom(this.http.put<Task>(`${this.url}/tasks/${id}/assigneeUserId`, {assigneeUserId}));
    await this.refreshCurrentPage();
  }

  async updateTaskName(id: string, name: string) {
    await lastValueFrom(this.http.put<Task>(`${this.url}/tasks/${id}/name`, {name}));
    await this.refreshCurrentPage();
  }

  async updateTaskStatus(id: string, status: string) {
    await lastValueFrom(this.http.put<Task>(`${this.url}/tasks/${id}/status`, {status}));
    await this.refreshCurrentPage();
  }

  async updateTaskDeadline(id: string, deadline: number | null) {
    await lastValueFrom(this.http.put<Task>(`${this.url}/tasks/${id}/deadline`, {deadline}));
    await this.refreshCurrentPage();
  }


  async createTask(name: string, description: string, status: string, assigneeUserId?: string, relationshipId?: string, relationshipType?: string, deadline?: number, delta?: any) {
    const response = await lastValueFrom(this.http.put<{ id: string, alias: string }>(`${this.url}/tasks/${ulid()}`, {
      name,
      description,
      status,
      relationshipId: relationshipId ?? null,
      relationshipType: relationshipType ?? null,
      assigneeUserId: assigneeUserId ?? null,
      deadline: deadline ?? null,
      delta: sanitizeDelta(delta)
    }));
    await this.refreshCurrentPage();
    return response;
  }

  async deleteTask(id: string) {
    await lastValueFrom(this.http.delete<{ id: string }>(`${this.url}/tasks/${id}`));
    await this.refreshCurrentPage();
  }

  async updateRelationship(taskId: string, relationshipId: string, type: string) {
    await lastValueFrom(this.http.put<Task>(`${this.url}/tasks/${taskId}/relationships/${relationshipId}`, {type}));
    await this.refreshCurrentPage();
  }

  async clearRelationships(taskId: string) {
    await lastValueFrom(this.http.delete<Task>(`${this.url}/tasks/${taskId}/relationships`));
    await this.refreshCurrentPage();
  }

  async getAllTaskRelationships(taskId: string = '*') {
    const result = await lastValueFrom(this.http.get<TaskRelationship[]>(this.url + `/tasks/${taskId}/relationships`));
    this.allRelationships.set(result);
    return result;
  }
}
